import React, { lazy } from 'react';
import { Phone, AlertTriangle, Snowflake, Droplets, Wind, Calendar, Layers } from 'lucide-react';
const PHONE = '586-203-3701';
interface Sign {
  Icon: typeof AlertTriangle;
  title: string;
  body: string;
  image: string;
  fallback?: string;
  imageAlt: string;
}
const SIGNS: Sign[] = [{
  Icon: AlertTriangle,
  title: 'Curling or Missing Shingles',
  body: 'Curled edges or bald spots mean your roof has reached the end of its life.',
  image: 'https://cdn.magicpatterns.com/uploads/3iJXpz8xxbKk1FPpSpEAXo/Curling_or_Missing_Shingles.jpg',
  imageAlt: 'Severely curled and deteriorating asphalt shingles on an aging residential roof'
}, {
  Icon: Droplets,
  title: 'Leaks or Water Stains',
  body: 'Brown ceiling stains or active drips mean water is getting past the shingles.',
  image: 'https://cdn.magicpatterns.com/uploads/dXiaZaPxkzFuFtvUwa23rT/Leaks_or_Water_Stains.jpg',
  imageAlt: 'Brown water stain on a white ceiling caused by a roof leak'
}, {
  Icon: Snowflake,
  title: 'Ice Dams in Winter',
  body: 'Common across Michigan — usually a sign of ventilation and insulation problems.',
  image: 'https://cdn.magicpatterns.com/uploads/35e3wVZ9Vnm5YmxAmxSMv1/Ice_Dams_in_Winter.jpg',
  imageAlt: 'Heavy ice dam with icicles forming along the gutter of a snow-covered Michigan roof'
}, {
  Icon: Wind,
  title: 'Storm or Hail Damage',
  body: "Even one strong Michigan storm can cause hidden damage that's easy to miss.",
  image: 'https://cdn.magicpatterns.com/uploads/55o7t6X6tbAgKLUfXnSuuT/Storm_and_Hail_Damage.jpg',
  imageAlt: 'Hail-damaged asphalt shingle roof showing impact marks and granule loss'
}, {
  Icon: Layers,
  title: 'Granules in Gutters',
  body: 'Excessive granule loss in your gutters means your shingles are wearing down.',
  image: 'https://cdn.magicpatterns.com/uploads/ivLX5LxYANXW1LC4TkqSuq/Granules_in_Gutters.webp',
  imageAlt: 'Close-up of shingle granules accumulating in a residential gutter — a sign of shingle wear'
}, {
  Icon: Calendar,
  title: 'Roof Is 20+ Years Old',
  body: 'Most asphalt shingle roofs need replacement after two decades of Michigan weather.',
  image: 'https://cdn.magicpatterns.com/uploads/mRQRM5rvJqfvmNQVPKzuwx/Roof_is_202B_Years.webp',
  imageAlt: 'Before and after of a Moceri roof replacement — an aged 20+ year old shingle roof transformed with new architectural shingles'
}];
export function RoofReplacement() {
  return <section className="bg-slate-50 py-20 sm:py-24" data-id="element-272">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-273">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-274">
          <span className="inline-block text-[#2DB8E0] font-bold uppercase tracking-wider text-sm mb-4" data-id="element-275">
            Roof Replacement
          </span>
          <h2 className="font-display italic text-4xl sm:text-5xl text-[#1a2332] leading-tight mb-4" data-id="element-276">
            Does Your Roof Need to Be Replaced?
          </h2>
          <p className="text-lg text-slate-600" data-id="element-277">
            If you're seeing any of these warning signs, don't wait. A free
            inspection from Moceri will give you a straight answer — no
            high-pressure sales tactics.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-278">
          {SIGNS.map(({
          Icon,
          title,
          body,
          image,
          fallback,
          imageAlt
        }) => <div key={title} className="group bg-white rounded-xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-id="element-279">
              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-slate-200" data-id="element-280">
                <img src={image} alt={imageAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => {
              if (fallback) {
                ;
                (e.currentTarget as HTMLImageElement).src = fallback;
              }
            }} data-id="element-281" />
                {/* Dark gradient for badge legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" data-id="element-282" />
                {/* Icon badge */}
                <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center" data-id="element-283">
                  <Icon className="w-5 h-5 text-[#2DB8E0]" data-id="element-284" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-285">
                <h3 className="font-bold text-[#1a2332] text-lg mb-2" data-id="element-286">
                  {title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-287">{body}</p>
              </div>
            </div>)}
        </div>

        <div className="text-center" data-id="element-288">
          <a href={`tel:${PHONE.replace(/-/g, '')}`} className="inline-flex items-center gap-3 bg-[#1a2332] hover:bg-[#2d3a4d] text-white font-bold text-base sm:text-lg px-6 sm:px-8 py-4 rounded-full transition-colors shadow-lg" data-id="element-289">
            <Phone className="w-5 h-5 text-[#2DB8E0]" data-id="element-290" />
            Call Now for a Free Inspection: {PHONE}
          </a>
        </div>
      </div>
    </section>;
}